import React, { useContext, useState } from 'react';
import {
  Caption1,
  Dropdown, Input, Option, makeStyles, mergeClasses, shorthands, tokens,
} from '@fluentui/react-components';
import { Search16Regular } from '@fluentui/react-icons';

import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { isValidUrl } from '@bingads-webui/url-util';

const protocols = ['https://', 'http://'];

const getProtocolFromFinalUrl = (url?: string) => (url
  ? protocols.find((protocol) => url.startsWith(protocol)) || protocols[0]
  : protocols[0]
);

const getUrlFromFinalUrl = (url?: string) => (url
  ? url.replace(getProtocolFromFinalUrl(url), '')
  : ''
);

const useEmptyViewStyles = makeStyles({
  group: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  dropdown: {
    minWidth: '95px',
  },
  input: {
    width: '100%',
    maxWidth: '300px',
  },
  error: {
    marginTop: tokens.spacingVerticalXS,
    color: tokens.colorPaletteRedForeground1,
  },
});

interface IFinalUrlProps {
  finalUrl?: string;
  onFinalUrlEntered: (finalUrl: string) => void;
}

export const FinalUrl: React.FC<IFinalUrlProps> = ({ finalUrl: initialFinalUrl, onFinalUrlEntered }) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const classes = useEmptyViewStyles();

  const [protocol, setProtocol] = useState(() => getProtocolFromFinalUrl(initialFinalUrl));
  const [finalUrl, setFinalUrl] = useState(() => getUrlFromFinalUrl(initialFinalUrl));

  const [isValid, setIsValid] = useState(true);

  const validateFinalUrl = (newFinalUrl: string) => {
    const finalUrlWithProtocol = `${protocol}${newFinalUrl}`;
    const isNewUrlValid = isValidUrl(finalUrlWithProtocol);

    setIsValid(isNewUrlValid);

    if (isNewUrlValid) {
      onFinalUrlEntered(finalUrlWithProtocol);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFinalUrl(e.currentTarget.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validateFinalUrl(e.currentTarget.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateFinalUrl(e.currentTarget.value);
    }
  };

  return (
    <div className="final-url">
      <div className={classes.group}>
        <Dropdown
          className={mergeClasses('protocol-dropdown', classes.dropdown)}
          value={protocol}
          onOptionSelect={(_, selected) => setProtocol(selected.optionValue!)}
        >
          {protocols.map((option) => <Option key={option}>{option}</Option>)}
        </Dropdown>
        <Input
          className={mergeClasses('final-url-input', classes.input)}
          contentBefore={<Search16Regular />}
          placeholder={i18n.getString(_TL_('Enter website URL'))}
          value={finalUrl}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
        />
      </div>
      {!isValid && (
        <Caption1 className={mergeClasses('suggestion-error-invalid', classes.error)}>
          {i18n.getString(_TL_('Please enter a valid Final URL to see recommendations.'))}
        </Caption1>
      )}
    </div>
  );
};
